import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Loader2, Droplets } from "lucide-react";
import { CHAT } from "@/constants/testIds";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  const k = "aquamatic_chat_session";
  let s = localStorage.getItem(k);
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    localStorage.setItem(k, s);
  }
  return s;
}

const WELCOME = {
  role: "assistant",
  content:
    "Hi! I'm Aqua, Aquamatic's virtual assistant. Ask me about our wireless controllers, installation, AMC or which product fits your building.",
};

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: getSessionId(),
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — I couldn't reach our systems just now. Please try again in a moment, or use the Contact form to reach us directly." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid={CHAT.toggle}
          aria-label="Open chat"
          className="chat-pulse fixed bottom-6 right-6 z-50 h-14 w-14 bg-[#002FA7] text-white flex items-center justify-center border-2 border-[#002FA7] hover:bg-[#00227A] transition-colors"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          data-testid={CHAT.panel}
          className="fixed bottom-6 right-6 z-50 w-[92vw] sm:w-[400px] h-[560px] max-h-[85vh] bg-white border border-zinc-300 shadow-[8px_8px_0px_0px_rgba(0,47,167,0.18)] flex flex-col"
        >
          <div className="flex items-center justify-between bg-[#002FA7] text-white px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/10 border border-white/20 flex items-center justify-center">
                <Droplets className="w-4 h-4" />
              </div>
              <div>
                <div className="font-semibold text-sm">Aqua Assistant</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/70">
                  Online • Aquamatic Inc.
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid={CHAT.close}
              aria-label="Close chat"
              className="p-1 hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={listRef} data-testid={CHAT.messages} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F4F4F5]">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed border ${
                    m.role === "user"
                      ? "bg-[#002FA7] text-white border-[#002FA7]"
                      : "bg-white text-zinc-900 border-zinc-200"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-zinc-200 px-3.5 py-2.5 text-sm text-zinc-500 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Aqua is typing…
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-zinc-200 p-3 flex items-end gap-2">
            <textarea
              data-testid={CHAT.input}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about products, installation, AMC…"
              className="flex-1 resize-none border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
            />
            <Button
              data-testid={CHAT.send}
              onClick={send}
              disabled={sending || !input.trim()}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none h-10 w-10 p-0 flex items-center justify-center"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
